import React, { useState, useRef, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CopyButtonProps {
  /** The text placed on the clipboard when clicked. */
  value: string;
  /** Extra classes for the trigger button. */
  className?: string;
}

/**
 * Small inline "copy to clipboard" button. Shows a check + "Copied" state for
 * ~2s after a successful copy, then reverts. Falls back silently if the
 * Clipboard API is unavailable (e.g. insecure context).
 */
const CopyButton: React.FC<CopyButtonProps> = ({ value, className = '' }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — no-op.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? t('copy_done') : t('copy_label')}
      title={copied ? t('copy_done') : t('copy_label')}
      className={`inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-[0.1em] transition-colors ${
        copied
          ? 'text-brand-success'
          : 'text-warm-500 hover:text-brand-accent'
      } ${className}`}
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5" />
          {t('copy_done')}
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5" />
          {t('copy_label')}
        </>
      )}
    </button>
  );
};

export default CopyButton;
